export interface Difficulty {
  id: number;
  key: string;
  name: string;
  rank: string;
  blurb: string;

  // Heat -----------------------------------------------------------------
  /** 0..1. Drives drying rate, ignition ease and burn rate. */
  ambientHeat: number;
  /** Shown in the briefing. */
  airTempC: number;

  // Wind -----------------------------------------------------------------
  /** 0..1 normalised wind strength. */
  windBase: number;
  /** How much the strength wanders. */
  windVar: number;
  /** Seconds between forecast wind shifts. */
  shiftInterval: number;
  /** Radians of swing per shift. */
  shiftMag: number;
  gustiness: number;

  // People ---------------------------------------------------------------
  residents: number;
  /** Fraction who refuse to leave until fire is close. */
  stubbornFrac: number;
  /** Cells: how near fire must come before people alert themselves. */
  alertRadius: number;

  // Fire -----------------------------------------------------------------
  ignitions: number;

  // Equipment ------------------------------------------------------------
  /** Litres, shown in HUD; one full tank is one drop. */
  tankLitres: number;
  /** Radius in cells wetted by a full drop. */
  dropRadius: number;
  /** Seconds to fill the bucket from the reservoir. */
  scoopTime: number;
  seats: number;
  /** Tank fraction burnt per second of flight. */
  fuelBurn: number;
  /** Tank fraction refuelled per second on the pad. */
  refuelRate: number;
  /** Seconds to swap between bucket and seats at the base. */
  swapTime: number;

  // Ground units ---------------------------------------------------------
  /** Autobombas dispatched to this incident. */
  engines: number;
  /** Seconds before the first engine reaches the map edge. */
  engineFirst: number;
  /** Seconds between subsequent arrivals. */
  engineStagger: number;
  /** Litres per engine tank, shown in the roster. */
  engineTank: number;
  /** Seconds of continuous work a tank lasts. */
  engineDrain: number;
  /** Seconds of hot work before a crew is spent. */
  crewEndurance: number;
  /** Seconds to draw a full tank from an acequia or alberca. */
  refillTime: number;

  /** Most of the mountain you may lose and still be graded A / B. */
  maxBurnA: number;
  maxBurnB: number;
}

export const BASE: Omit<Difficulty, 'id' | 'key' | 'name' | 'rank' | 'blurb'> = {
  ambientHeat: 0.2,
  airTempC: 26,
  windBase: 0.22,
  windVar: 0.08,
  shiftInterval: 150,
  shiftMag: 0.5,
  gustiness: 0.15,
  residents: 24,
  stubbornFrac: 0,
  alertRadius: 22,
  ignitions: 1,
  tankLitres: 1200,
  dropRadius: 6,
  scoopTime: 2.0,
  seats: 8,
  fuelBurn: 1 / 600,
  refuelRate: 0.3,
  swapTime: 3,
  engines: 3,
  engineFirst: 30,
  engineStagger: 25,
  engineTank: 3500,
  engineDrain: 150,
  crewEndurance: 260,
  refillTime: 14,
  maxBurnA: 0.18,
  maxBurnB: 0.34,
};

export const DIFFICULTIES: readonly Difficulty[] = [
  {
    ...BASE,
    id: 0,
    key: 'trainee',
    name: 'Práctica',
    rank: 'Trainee',
    blurb:
      'A quiet August afternoon. One fire, low in the barranco, which is the dry stony gully below the village. A light breeze is blowing up the slope, and you have time to think. Both roads are open and the helicopter has a full tank.',
  },
  {
    ...BASE,
    id: 1,
    key: 'bombero',
    name: 'Bombero',
    rank: 'Firefighter',
    ambientHeat: 0.38,
    airTempC: 33,
    windBase: 0.28,
    windVar: 0.1,
    shiftInterval: 130,
    shiftMag: 0.55,
    gustiness: 0.22,
    residents: 42,
    stubbornFrac: 0.08,
    alertRadius: 18,
    ignitions: 1,
    tankLitres: 1000,
    dropRadius: 5,
    scoopTime: 2.4,
    seats: 6,
    fuelBurn: 1 / 460,
    engines: 3,
    engineFirst: 45,
    engineStagger: 30,
    engineTank: 3000,
    engineDrain: 130,
    crewEndurance: 210,
    refillTime: 16,
    maxBurnA: 0.24,
    maxBurnB: 0.42,
    blurb:
      'Thirty-three degrees. The breeze off the valley has begun to blow steadily up the slope, which pushes a fire uphill fast. The fire is in the pine woods below Lanjarón, and the road is about to fill with cars.',
  },
  {
    ...BASE,
    id: 2,
    key: 'jefe',
    name: 'Jefe de Sección',
    rank: 'Section Chief',
    ambientHeat: 0.52,
    airTempC: 39,
    windBase: 0.36,
    windVar: 0.13,
    shiftInterval: 105,
    shiftMag: 0.7,
    gustiness: 0.3,
    residents: 64,
    stubbornFrac: 0.15,
    alertRadius: 14,
    ignitions: 2,
    tankLitres: 1000,
    dropRadius: 5.2,
    scoopTime: 2.4,
    seats: 6,
    fuelBurn: 1 / 500,
    refuelRate: 0.3,
    swapTime: 3.5,
    engines: 3,
    engineFirst: 55,
    engineStagger: 35,
    engineTank: 3000,
    engineDrain: 140,
    crewEndurance: 230,
    refillTime: 15,
    maxBurnA: 0.3,
    maxBurnB: 0.5,
    blurb:
      'Thirty-nine degrees and two separate fires. Sixty-four people are on the mountain, you have three fire engines, and the wind keeps changing direction.',
  },
  {
    ...BASE,
    id: 3,
    key: 'infierno',
    name: 'Infierno',
    rank: 'Incident Commander',
    ambientHeat: 0.64,
    airTempC: 44,
    windBase: 0.44,
    windVar: 0.16,
    shiftInterval: 85,
    shiftMag: 0.85,
    gustiness: 0.38,
    residents: 78,
    stubbornFrac: 0.14,
    alertRadius: 14,
    ignitions: 3,
    tankLitres: 900,
    dropRadius: 4.8,
    scoopTime: 2.7,
    seats: 5,
    fuelBurn: 1 / 420,
    refuelRate: 0.28,
    swapTime: 4,
    engines: 4,
    engineFirst: 60,
    engineStagger: 35,
    engineTank: 2800,
    engineDrain: 130,
    crewEndurance: 200,
    refillTime: 17,
    maxBurnA: 0.36,
    maxBurnB: 0.58,
    blurb:
      'Forty-four degrees, and a terral, the hot dry wind that blows down off the mountains. Three separate fires, and seventy-eight people spread from the farm terraces up to Cortijo del Cura, the highest house on the mountain. No other crews are available.',
  },
];

export const difficultyByKey = (key: string): Difficulty =>
  DIFFICULTIES.find((d) => d.key === key) ?? DIFFICULTIES[0];

// ---------------------------------------------------------------------------
// Custom incident
// ---------------------------------------------------------------------------

/** Everything the player can set for themselves, in the units they read. */
export interface CustomOptions {
  airTempC: number;
  windKmh: number;
  shiftSeconds: number;
  residents: number;
  stubbornPct: number;
  ignitions: number;
  tankLitres: number;
  seats: number;
  engines: number;
  engineFirst: number;
}

/** Ranges the sliders offer, and what each one actually does. */
export const CUSTOM_FIELDS: readonly {
  key: keyof CustomOptions;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  help: string;
}[] = [
  { key: 'airTempC', label: 'Air temperature', min: 18, max: 48, step: 1, unit: '°C', help: 'Hotter dries the fuel out and makes everything catch faster.' },
  { key: 'windKmh', label: 'Wind', min: 0, max: 60, step: 1, unit: ' km/h', help: 'Decides which side of the fire spreads fastest. It never pushes your helicopter off course.' },
  { key: 'shiftSeconds', label: 'Wind shifts every', min: 40, max: 240, step: 5, unit: 's', help: 'How often the wind changes direction. A shorter time gives you less warning.' },
  { key: 'residents', label: 'People on the mountain', min: 8, max: 150, step: 1, unit: '', help: 'Spread across the eight villages, from Lanjarón at the bottom up to Cortijo del Cura at the top.' },
  { key: 'stubbornPct', label: 'Refuse to leave', min: 0, max: 50, step: 1, unit: '%', help: 'They stay put until the fire is nearly on them.' },
  { key: 'ignitions', label: 'Separate ignitions', min: 1, max: 6, step: 1, unit: '', help: 'Each fire starts low in the gully, well away from the houses.' },
  { key: 'tankLitres', label: 'Helicopter bucket', min: 400, max: 2000, step: 50, unit: ' l', help: 'A bigger bucket wets more ground per drop.' },
  { key: 'seats', label: 'Helicopter seats', min: 2, max: 12, step: 1, unit: '', help: 'How many people you can carry from the pickup point in one trip.' },
  { key: 'engines', label: 'Fire engines', min: 0, max: 6, step: 1, unit: '', help: 'They can only travel on the road, but they carry out an order without further input from you.' },
  { key: 'engineFirst', label: 'First engine arrives', min: 0, max: 240, step: 5, unit: 's', help: 'Get them there before the first building catches.' },
];

export const CUSTOM_DEFAULTS: CustomOptions = {
  airTempC: 34,
  windKmh: 20,
  shiftSeconds: 120,
  residents: 45,
  stubbornPct: 8,
  ignitions: 2,
  tankLitres: 1100,
  seats: 6,
  engines: 3,
  engineFirst: 45,
};

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Turn the player's settings into a full difficulty definition. */
export function makeCustom(o: CustomOptions): Difficulty {
  const heat = clamp01((o.airTempC - 18) / 30);
  const wind = clamp01(o.windKmh / 60);
  // Everything the player did not set scales sensibly off what they did.
  return {
    ...BASE,
    id: 4,
    key: 'custom',
    name: 'A medida',
    rank: 'Custom incident',
    blurb: 'Your own incident. You set the weather, how many people are on the mountain, and what you get to fight the fire with.',
    ambientHeat: heat,
    airTempC: o.airTempC,
    windBase: wind,
    windVar: wind * 0.35,
    shiftInterval: o.shiftSeconds,
    shiftMag: 0.5 + wind * 0.8,
    gustiness: 0.12 + wind * 0.5,
    residents: o.residents,
    stubbornFrac: o.stubbornPct / 100,
    alertRadius: 22 - heat * 8,
    ignitions: o.ignitions,
    tankLitres: o.tankLitres,
    dropRadius: 3.4 + (o.tankLitres / 2000) * 3.4,
    scoopTime: 1.8 + (o.tankLitres / 2000) * 1.6,
    seats: o.seats,
    engines: o.engines,
    engineFirst: o.engineFirst,
    engineStagger: 25 + heat * 20,
    // Losing more of a big mountain is forgivable when the odds are stacked.
    maxBurnA: 0.16 + heat * 0.22,
    maxBurnB: 0.32 + heat * 0.3,
  };
}




